import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config';

function PasswordResetRequests({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [processing, setProcessing] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(
        `${API_ENDPOINTS.USERS}?type=reset_requests`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setRequests(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch reset requests:', err);
      setError('Failed to load password reset requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      const response = await axios.put(
        API_ENDPOINTS.USERS,
        {
          action: 'reset_request_response',
          request_id: selectedRequest.id,
          user_id: selectedRequest.user_id,
          response: 'APPROVED',
          admin_response: adminResponse,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setTempPassword(response.data.temp_password);
        setTimeout(() => {
          alert(`✅ Password reset approved!\n\nNew temporary password: ${response.data.temp_password}\n\nPassword has been sent to user's email.`);
          setShowApproveModal(false);
          setSelectedRequest(null);
          setAdminResponse('');
          setTempPassword('');
          fetchRequests();
        }, 500);
      }
    } catch (err) {
      console.error('Failed to approve request:', err);
      alert('❌ Failed to approve: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !adminResponse) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.put(
        API_ENDPOINTS.USERS,
        {
          action: 'reset_request_response',
          request_id: selectedRequest.id,
          user_id: selectedRequest.user_id,
          response: 'REJECTED',
          admin_response: adminResponse,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        alert('✅ Password reset request rejected!');
        setShowRejectModal(false);
        setSelectedRequest(null);
        setAdminResponse('');
        fetchRequests();
      }
    } catch (err) {
      console.error('Failed to reject request:', err);
      alert('❌ Failed to reject: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  if (user.role !== 'admin') {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this page. Only admins can manage password reset requests.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Password Reset Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Review and approve password reset requests from users
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={fetchRequests} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LockIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Pending Requests
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {requests.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* No Requests */}
      {!loading && requests.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Pending Password Reset Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All password reset requests have been processed.
          </Typography>
        </Paper>
      )}

      {/* Requests Table */}
      {!loading && requests.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1565c0' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Requested At</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading requests...</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>#{request.id}</TableCell>
                    <TableCell><strong>{request.full_name}</strong></TableCell>
                    <TableCell>{request.email}</TableCell>
                    <TableCell>
                      <Chip label={request.role} size="small" />
                    </TableCell>
                    <TableCell>{new Date(request.requested_at).toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowApproveModal(true);
                        }}
                      >
                        <ApproveIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowRejectModal(true);
                        }}
                      >
                        <RejectIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approve Modal */}
      <Dialog
        open={showApproveModal}
        onClose={() => {
          if (!processing) {
            setShowApproveModal(false);
            setSelectedRequest(null);
            setAdminResponse('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          Approve Password Reset
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            A new temporary password will be generated and sent to the user's email.
          </Alert>
          {selectedRequest && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>User:</strong> {selectedRequest.full_name}<br/>
              <strong>Email:</strong> {selectedRequest.email}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (Optional)"
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            placeholder="Add any comments about this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowApproveModal(false);
            setSelectedRequest(null);
            setAdminResponse('');
          }} disabled={processing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <ApproveIcon />}
          >
            {processing ? 'Approving...' : 'Approve & Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog
        open={showRejectModal}
        onClose={() => {
          if (!processing) {
            setShowRejectModal(false);
            setSelectedRequest(null);
            setAdminResponse('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          Reject Password Reset
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this password reset request.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason *"
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            placeholder="Explain why you're rejecting this request..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowRejectModal(false);
            setSelectedRequest(null);
            setAdminResponse('');
          }} disabled={processing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing || !adminResponse}
            startIcon={processing ? <CircularProgress size={20} /> : <RejectIcon />}
          >
            {processing ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PasswordResetRequests;